import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { vnextApiClient } from '@/services/vnext-api';

export type CaseRedirectStatus = 'resolving' | 'fallback';

/**
 * Resolve an entity's codename family and redirect the old standalone
 * detail route into the canonical case page:
 *
 *     /admin/triage/{id}         →  /cases/{family}?item=rr:{id}
 *     /admin/enquiries/{id}      →  /cases/{family}?item=enq:{id}
 *
 * Existing query params (?openChat, ?section, …) are carried across.
 * When the entity resolves no family (orphan; belt-and-braces — post
 * uncoded-elimination this shouldn't happen) or the resolve call fails,
 * returns 'fallback' so the standalone page renders as before.
 */
export function useCaseRedirect(
  entityType: 'enquiry' | 'report_request',
  itemPrefix: 'enq' | 'rr',
  entityId: string | undefined
): CaseRedirectStatus {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState<CaseRedirectStatus>('resolving');

  useEffect(() => {
    let cancelled = false;
    if (!entityId) {
      setStatus('fallback');
      return;
    }
    void (async () => {
      try {
        const family = await vnextApiClient.resolveCase(entityType, entityId);
        if (cancelled) {
          return;
        }
        const params = new URLSearchParams(location.search);
        params.set('item', `${itemPrefix}:${entityId}`);
        navigate(`/cases/${encodeURIComponent(family)}?${params.toString()}`, {
          replace: true,
        });
      } catch {
        if (!cancelled) {
          setStatus('fallback');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // location.search deliberately captured once — re-resolving on every
    // query-param change would loop against the navigate above.
  }, [entityType, itemPrefix, entityId, navigate]);

  return status;
}
